import { getAccessToken, renewAccessToken } from '@/lib/auth/tokenStore';

/** One Server-Sent Event: its name (`message` when unnamed) and its data. */
export interface StreamEvent {
  event: string;
  data: string;
}

/**
 * Splits Server-Sent Events text into whole events.
 *
 * Returns the events completed by `text` and whatever is left over, to be
 * prepended to the next chunk: a network chunk can end mid-event. Comment
 * lines (`: keep-alive`) and `id:`/`retry:` fields are ignored; multi-line
 * `data:` is joined with newlines, as the standard says.
 */
export function parseEvents(
  buffered: string,
  text: string,
): { events: StreamEvent[]; rest: string } {
  const all = (buffered + text).replace(/\r\n?/g, '\n');
  const blocks = all.split('\n\n');
  const rest = blocks.pop() ?? '';
  const events: StreamEvent[] = [];
  for (const block of blocks) {
    let event = 'message';
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (line === '' || line.startsWith(':')) continue;
      const colon = line.indexOf(':');
      const field = colon === -1 ? line : line.slice(0, colon);
      const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
      if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
    }
    if (data.length > 0) events.push({ event, data: data.join('\n') });
  }
  return { events, rest };
}

/** The wait before reconnect attempt `n` (0-based): 1s, 2s, 4s … capped at 30s. */
export function backoffMs(attempt: number): number {
  return Math.min(30_000, 1000 * 2 ** attempt);
}

/**
 * Holds a Server-Sent Events stream open, with the bearer token.
 *
 * `EventSource` cannot send an `Authorization` header, and a token in the
 * query string would land in proxy logs, so the stream is read with `fetch`
 * (research R1). A 401 renews the token once and retries; any other end of
 * the stream reconnects after a backoff that resets once a stream opens.
 * Aborting `signal` stops it for good.
 */
export function holdEventStream({
  url,
  signal,
  onEvent,
  onOpen,
  wait = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    }),
}: {
  url: string;
  signal: AbortSignal;
  onEvent: (event: StreamEvent) => void;
  /** Called each time a stream opens; a reconnect may have missed messages. */
  onOpen?: () => void;
  wait?: (ms: number) => Promise<unknown>;
}): Promise<void> {
  const connect = (token: string | null) =>
    fetch(url, {
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal,
    });

  return (async () => {
    let attempt = 0;
    while (!signal.aborted) {
      try {
        let response = await connect(getAccessToken());
        if (response.status === 401 && getAccessToken()) {
          const renewed = await renewAccessToken();
          if (renewed) response = await connect(renewed);
        }
        if (response.status === 401) return; // signed out: nothing to retry
        if (!response.ok || !response.body) throw new Error('stream refused');
        attempt = 0;
        onOpen?.();
        // A plain decoder rather than `TextDecoderStream`, which not every
        // runtime this code meets (jsdom among them) provides.
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let rest = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          const parsed = parseEvents(
            rest,
            decoder.decode(value, { stream: true }),
          );
          rest = parsed.rest;
          parsed.events.forEach(onEvent);
        }
      } catch {
        if (signal.aborted) return;
      }
      if (signal.aborted) return;
      await wait(backoffMs(attempt));
      attempt += 1;
    }
  })();
}
